function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function roundMoney(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function moneyLabel(value, currency = "ARS") {
  const code = String(currency || "ARS").toUpperCase();
  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: code,
      maximumFractionDigits: code === "ARS" ? 0 : 2,
    }).format(Number(value || 0));
  } catch {
    return `${code} ${Number(value || 0).toLocaleString("es-AR")}`;
  }
}

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "long" }).format(date);
}

function addCurrency(target, currency, amount) {
  const code = String(currency || "ARS").toUpperCase();
  target[code] = roundMoney(Number(target[code] || 0) + Number(amount || 0));
}

function activeContractTerms(agreement = {}) {
  return (agreement?.terms || []).filter((term) => (
    term.status !== "cancelled"
    && term.billingMode !== "monthly"
    && term.billingMode !== "included"
    && Number(term.expectedTotal ?? term.amount ?? 0) > 0
  ));
}

function commonParties(agreement = {}) {
  const client = agreement?.initiative?.client || null;
  return {
    issuer: {
      name: agreement?.issuer?.name || "Owner WebProfit",
      email: agreement?.issuer?.email || "",
    },
    client: client ? {
      name: client.name || "",
      company: client.company || "",
      email: client.email || "",
      phone: client.phone || "",
    } : null,
    project: agreement?.initiative?.name || "Proyecto",
  };
}

export function buildContractPrintDocument(agreement = {}) {
  const terms = activeContractTerms(agreement);
  const totals = {};
  const paid = {};
  const outstanding = {};

  const lines = terms.map((term, index) => {
    const currency = String(term.currency || "ARS").toUpperCase();
    const expected = roundMoney(term.expectedTotal ?? term.amount ?? 0);
    const paidAmount = roundMoney(term.paidInTermCurrency || 0);
    const pending = roundMoney(Math.max(0, term.outstanding ?? (expected - paidAmount)));
    addCurrency(totals, currency, expected);
    addCurrency(paid, currency, paidAmount);
    addCurrency(outstanding, currency, pending);
    return {
      label: `${term.sequence ? `${term.sequence}. ` : `${index + 1}. `}${term.title || "Contrato"}`,
      detail: term.billingMode === "installments"
        ? `${term.installmentCount || "—"} cuotas · ${term.installmentFrequency === "weekly" ? "semanales" : "mensuales"}`
        : "Pago único",
      amount: expected,
      currency,
    };
  });

  return {
    kind: "contract",
    title: "Factura global del contrato",
    disclaimer: "Documento comercial interno · no es comprobante fiscal",
    reference: `CONTRATO-${idOf(agreement?.initiative).slice(-8).toUpperCase() || "OWNER"}`,
    issuedAt: new Date(),
    ...commonParties(agreement),
    lines,
    totals,
    paid,
    outstanding,
    notes: "Resume el acuerdo económico vigente del contrato. Las mensualidades recurrentes se documentan por separado.",
  };
}

export function buildInstallmentReceiptDocument(agreement = {}, payment = {}) {
  const terms = agreement?.terms || [];
  const financingTermId = agreement?.financial?.contractProgress?.financing?.termId || null;
  const term = terms.find((row) => idOf(row) === idOf(payment.projectTermId || financingTermId)) || null;
  const plans = agreement?.paymentPlans || [];
  const plan = plans.find((row) => idOf(row.linkedTransactionId) === idOf(payment))
    || plans.find((row) => Number(row.scheduleIndex || 0) === Number(payment.installmentNumber || 0) && idOf(row.projectTermId) === idOf(term));
  const installmentCount = Number(term?.installmentCount || agreement?.financial?.contractProgress?.financing?.installmentCount || 0);
  const installmentNumber = Number(payment.installmentNumber || plan?.scheduleIndex || 0) || null;
  const referenceId = idOf(payment) || idOf(plan);

  return {
    kind: "installment_receipt",
    title: "Comprobante de pago de cuota",
    disclaimer: "Constancia comercial de pago · no es comprobante fiscal",
    reference: `CUOTA-${referenceId.slice(-8).toUpperCase() || "OWNER"}`,
    issuedAt: new Date(),
    paidAt: payment.occurredAt || plan?.paidAt || null,
    ...commonParties(agreement),
    termTitle: term?.title || "Contrato",
    periodLabel: installmentNumber
      ? `Cuota ${installmentNumber}${installmentCount ? ` de ${installmentCount}` : ""}`
      : "Cuota del contrato",
    amount: roundMoney(payment.amount ?? plan?.amount ?? 0),
    currency: String(payment.currency || plan?.currency || term?.installmentCurrency || term?.currency || "ARS").toUpperCase(),
    contractValue: Number(payment.contractValue || 0) > 0 ? roundMoney(payment.contractValue) : null,
    contractCurrency: payment.contractCurrency || term?.currency || null,
    fxRateARSPerUSD: payment.fxRateARSPerUSD || null,
    description: payment.description || plan?.title || "Pago realizado",
  };
}

export function monthlyPaymentsForTerm(agreement = {}, termId) {
  const targetId = idOf(termId);
  if (!targetId) return [];
  return (agreement?.payments || [])
    .filter((payment) => idOf(payment.projectTermId) === targetId)
    .filter((payment) => (
      String(payment?.source?.kind || "") === "recurring"
      || idOf(payment?.metadata?.monthlyAgreementHistory?.projectTermId) === targetId
      || Boolean(payment.recurringPlanId)
    ))
    .slice()
    .sort((a, b) => new Date(b.occurredAt || 0) - new Date(a.occurredAt || 0));
}

export function buildMonthlyReceiptDocument(agreement = {}, term = {}, payment = {}) {
  const periodKey = String(
    payment?.metadata?.monthlyAgreementHistory?.periodKey
    || payment?.recurringPeriodKey
    || (payment?.occurredAt ? String(payment.occurredAt).slice(0, 7) : ""),
  );
  const referenceId = idOf(payment);
  return {
    kind: "monthly_receipt",
    title: "Comprobante de pago de mensualidad",
    disclaimer: "Constancia comercial de pago · no es comprobante fiscal",
    reference: `MENSUAL-${referenceId.slice(-8).toUpperCase() || "OWNER"}`,
    issuedAt: new Date(),
    paidAt: payment.occurredAt || null,
    ...commonParties(agreement),
    termTitle: term?.title || "Mensualidad",
    periodLabel: periodKey ? `Período ${periodKey}` : "Mensualidad",
    amount: roundMoney(payment.amount || 0),
    currency: String(payment.currency || term?.currency || "ARS").toUpperCase(),
    description: payment.description || payment.category || term?.title || "Mensualidad abonada",
  };
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function currencyRows(values = {}, label) {
  return Object.entries(values || {}).map(([currency, amount]) => `
    <div class="summary-row"><span>${escapeHtml(label)}</span><strong>${escapeHtml(moneyLabel(amount, currency))}</strong></div>
  `).join("");
}

function partyHtml(label, party) {
  if (!party) return `
    <section class="party"><div class="eyebrow">${escapeHtml(label)}</div><strong>Sin datos cargados</strong></section>
  `;
  return `
    <section class="party">
      <div class="eyebrow">${escapeHtml(label)}</div>
      <strong>${escapeHtml(party.company || party.name || "—")}</strong>
      ${party.company && party.name ? `<div>${escapeHtml(party.name)}</div>` : ""}
      ${party.email ? `<div>${escapeHtml(party.email)}</div>` : ""}
      ${party.phone ? `<div>${escapeHtml(party.phone)}</div>` : ""}
    </section>
  `;
}

function contractBody(document) {
  const rows = (document.lines || []).map((line) => `
    <tr>
      <td><strong>${escapeHtml(line.label)}</strong>${line.detail ? `<div class="muted">${escapeHtml(line.detail)}</div>` : ""}</td>
      <td class="amount">${escapeHtml(moneyLabel(line.amount, line.currency))}</td>
    </tr>
  `).join("");
  return `
    <table><thead><tr><th>Concepto</th><th class="amount">Importe</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="summary">
      ${currencyRows(document.totals, "Total acordado")}
      ${currencyRows(document.paid, "Cobrado")}
      ${currencyRows(document.outstanding, "Pendiente")}
    </div>
    ${document.notes ? `<p class="note">${escapeHtml(document.notes)}</p>` : ""}
  `;
}

function receiptBody(document) {
  const contractEquivalent = document.contractValue && document.contractCurrency
    ? `<div class="summary-row"><span>Valor imputado al contrato</span><strong>${escapeHtml(moneyLabel(document.contractValue, document.contractCurrency))}</strong></div>`
    : "";
  const fx = document.fxRateARSPerUSD
    ? `<div class="summary-row"><span>Cotización histórica</span><strong>${escapeHtml(moneyLabel(document.fxRateARSPerUSD, "ARS"))} / USD</strong></div>`
    : "";
  return `
    <div class="receipt-amount">${escapeHtml(moneyLabel(document.amount, document.currency))}</div>
    <div class="receipt-label">${escapeHtml(document.periodLabel || "Pago realizado")}</div>
    <div class="summary">
      <div class="summary-row"><span>Fecha real del pago</span><strong>${escapeHtml(dateLabel(document.paidAt))}</strong></div>
      <div class="summary-row"><span>Concepto</span><strong>${escapeHtml(document.termTitle || document.description || "Pago")}</strong></div>
      ${contractEquivalent}
      ${fx}
    </div>
    ${document.description ? `<p class="note">${escapeHtml(document.description)}</p>` : ""}
  `;
}

function documentHtml(document) {
  const body = document.kind === "contract" ? contractBody(document) : receiptBody(document);
  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${escapeHtml(document.title || "Comprobante")}</title>
<style>
  *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;margin:0;color:#111;background:#fff}
  .page{max-width:820px;margin:0 auto;padding:42px 48px}
  .top{display:flex;justify-content:space-between;gap:24px;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:20px}
  h1{font-size:27px;line-height:1.1;margin:0 0 8px}.badge{display:inline-block;border:1px solid #777;border-radius:999px;padding:5px 9px;font-size:10px;font-weight:700;letter-spacing:.04em}
  .meta{text-align:right;font-size:12px;line-height:1.5}.muted{color:#666;font-size:11px;margin-top:4px}.eyebrow{font-size:10px;font-weight:700;letter-spacing:.08em;color:#666;margin-bottom:7px}
  .parties{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin:22px 0}.party{border:1px solid #ddd;border-radius:12px;padding:14px;font-size:12px;line-height:1.5}.party strong{display:block;font-size:15px;margin-bottom:2px}
  .project{margin:0 0 18px;font-size:12px}.project strong{font-size:14px}
  table{width:100%;border-collapse:collapse;margin-top:16px}th,td{padding:12px 10px;border-bottom:1px solid #ddd;text-align:left;font-size:12px}th{font-size:10px;text-transform:uppercase;letter-spacing:.06em}.amount{text-align:right;white-space:nowrap}
  .summary{margin:18px 0 0 auto;max-width:390px;border-top:2px solid #111;padding-top:8px}.summary-row{display:flex;justify-content:space-between;gap:20px;padding:7px 0;border-bottom:1px solid #eee;font-size:12px}
  .note{font-size:11px;line-height:1.5;color:#555;margin-top:24px}.receipt-amount{font-size:38px;font-weight:800;margin-top:28px}.receipt-label{font-size:14px;font-weight:700;margin-top:6px}
  .footer{margin-top:38px;padding-top:14px;border-top:1px solid #ddd;font-size:10px;color:#666;display:flex;justify-content:space-between;gap:16px}
  @media print{.page{max-width:none;padding:24px 28px}@page{size:A4;margin:10mm}}
  @media(max-width:640px){.page{padding:26px 20px}.top,.parties{display:block}.meta{text-align:left;margin-top:14px}.party{margin-top:10px}}
</style>
</head>
<body>
<main class="page">
  <header class="top">
    <div><h1>${escapeHtml(document.title)}</h1><span class="badge">${escapeHtml(document.disclaimer || "Documento comercial")}</span></div>
    <div class="meta"><strong>${escapeHtml(document.reference || "")}</strong><br/>Emitido ${escapeHtml(dateLabel(document.issuedAt))}</div>
  </header>
  <div class="parties">${partyHtml("EMISOR", document.issuer)}${partyHtml("CLIENTE", document.client)}</div>
  <div class="project"><span class="eyebrow">PROYECTO</span><strong>${escapeHtml(document.project || "—")}</strong></div>
  ${body}
  <footer class="footer"><span>Generado desde Owner · WebProfit</span><span>${escapeHtml(document.reference || "")}</span></footer>
</main>
<script>window.addEventListener('load',()=>{setTimeout(()=>window.print(),120)});</script>
</body>
</html>`;
}

export function printOwnerProjectDocument(document) {
  if (!document) return false;
  const popup = window.open("", "_blank");
  if (!popup) return false;
  try { popup.opener = null; } catch {}
  popup.document.open();
  popup.document.write(documentHtml(document));
  popup.document.close();
  return true;
}
